import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getApiBase, getActingOrganizationId } from '../utils/api';
import { useAuth } from './AuthContext';

// SSE Event Types (matching backend)
export const SSEEventType = {
  // Sticky notes
  STICKY_NOTE_CREATED: 'sticky_note_created',
  STICKY_NOTE_UPDATED: 'sticky_note_updated',
  STICKY_NOTE_DELETED: 'sticky_note_deleted',

  // Todos
  TODO_CREATED: 'todo_created',
  TODO_UPDATED: 'todo_updated',
  TODO_DELETED: 'todo_deleted',

  // Unfiled/Unfilled components
  UNFILED_COMPONENT_CREATED: 'unfiled_component_created',
  UNFILED_COMPONENT_UPDATED: 'unfiled_component_updated',
  UNFILED_COMPONENT_ASSIGNED: 'unfiled_component_assigned',
  UNFILED_COMPONENT_DELETED: 'unfiled_component_deleted',

  // Trip components
  COMPONENT_CREATED: 'component_created',
  COMPONENT_UPDATED: 'component_updated',
  COMPONENT_DELETED: 'component_deleted',

  // Trips
  TRIP_CREATED: 'trip_created',
  TRIP_UPDATED: 'trip_updated',
  TRIP_DELETED: 'trip_deleted',

  // Flight real-time updates (from FlightAware)
  FLIGHT_STATUS_UPDATED: 'flight_status_updated',
  FLIGHT_GATE_UPDATED: 'flight_gate_updated',
  FLIGHT_TIME_UPDATED: 'flight_time_updated',
  FLIGHT_DEPARTED: 'flight_departed',
  FLIGHT_ARRIVED: 'flight_arrived',
  FLIGHT_DIVERTED: 'flight_diverted',
  FLIGHT_CANCELLED: 'flight_cancelled',

  // Urbanride chauffeur service updates (from Urbanride webhooks)
  URBANRIDE_STATUS_UPDATED: 'urbanride_status_updated',
  URBANRIDE_CHAUFFEUR_ASSIGNED: 'urbanride_chauffeur_assigned',
  URBANRIDE_CHAUFFEUR_UNASSIGNED: 'urbanride_chauffeur_unassigned',
  URBANRIDE_VEHICLE_ON_LOCATION: 'urbanride_vehicle_on_location',
  URBANRIDE_PASSENGER_ON_BOARD: 'urbanride_passenger_on_board',
  URBANRIDE_RIDE_COMPLETED: 'urbanride_ride_completed',

  // Parsing review queue (admin)
  REVIEW_QUEUE_ITEM_CREATED: 'review_queue_item_created',
  REVIEW_QUEUE_ITEM_UPDATED: 'review_queue_item_updated',
  REVIEW_QUEUE_ITEM_ASSIGNED: 'review_queue_item_assigned',
  REVIEW_QUEUE_ITEM_COMPLETED: 'review_queue_item_completed',
  REVIEW_QUEUE_ITEM_DELETED: 'review_queue_item_deleted',
  REVIEW_QUEUE_BULK_DELETED: 'review_queue_bulk_deleted',

  // Connection lifecycle
  CONNECTED: 'connected',
  HEARTBEAT: 'heartbeat',
};

const SSEContext = createContext();

export const useSSE = () => {
  const context = useContext(SSEContext);
  if (!context) {
    throw new Error('useSSE must be used within an SSEProvider');
  }
  return context;
};

export const SSEProvider = ({ children }) => {
  const { user } = useAuth();
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const [connectionError, setConnectionError] = useState(null);

  // Use refs to store event listeners and EventSource
  const eventSourceRef = useRef(null);
  const listenersRef = useRef({});
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);

  // Subscribe to specific event types
  const subscribe = useCallback((eventType, callback) => {
    if (!listenersRef.current[eventType]) {
      listenersRef.current[eventType] = new Set();
    }
    listenersRef.current[eventType].add(callback);
    console.log(`📝 Subscribed to ${eventType}, total listeners: ${listenersRef.current[eventType].size}`);

    // Return unsubscribe function
    return () => {
      if (listenersRef.current[eventType]) {
        listenersRef.current[eventType].delete(callback);
        console.log(`📝 Unsubscribed from ${eventType}, remaining listeners: ${listenersRef.current[eventType].size}`);
      }
    };
  }, []);

  // Notify all listeners for an event type
  const notifyListeners = useCallback((eventType, data) => {
    const listeners = listenersRef.current[eventType];
    console.log(`📣 notifyListeners called for ${eventType}, listeners count: ${listeners ? listeners.size : 0}`);
    if (listeners) {
      listeners.forEach((callback) => {
        try {
          console.log(`📣 Calling listener for ${eventType}`);
          callback(data);
        } catch (error) {
          console.error(`Error in SSE listener for ${eventType}:`, error);
        }
      });
    }
  }, []);

  // Connect to SSE endpoint
  const connect = useCallback((tripId = null) => {
    // Don't connect if not authenticated
    if (!user) {
      return;
    }

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    // Clear any pending reconnect
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    try {
      const apiBase = getApiBase();
      // Remove /api suffix to get base URL, then add back /api/events
      const baseUrl = apiBase.replace(/\/api$/, '');
      let sseUrl = `${baseUrl}/api/events`;

      // Build query parameters
      const params = new URLSearchParams();

      // Add trip_id parameter if provided
      if (tripId) {
        params.append('trip_id', tripId);
      }

      // Scope the stream to the acting organization. EventSource can't send the
      // X-Acting-Organization-Id header, so pass it as a query param - otherwise
      // a superadmin (or multi-org user) viewing another org would receive events
      // for their primary org and never see live inbox/timeline updates for the
      // org they switched to (they'd only appear after a manual refresh, whose
      // REST call does carry the acting-org header).
      const actingOrgId = getActingOrganizationId();
      if (actingOrgId) {
        params.append('organization_id', actingOrgId);
      }

      // Build final URL with query params
      const queryString = params.toString();
      if (queryString) {
        sseUrl += `?${queryString}`;
      }

      // Create EventSource with credentials (cookies) support
      // Authentication works via:
      // 1. Token query parameter (for SAML/Safari users)
      // 2. session_token cookie (for regular login users)
      const eventSource = new EventSource(sseUrl, {
        withCredentials: true,
      });

      eventSource.onopen = () => {
        console.log('SSE: Connected');
        setConnected(true);
        setConnectionError(null);
        reconnectAttemptsRef.current = 0;
      };

      eventSource.onerror = (error) => {
        // Log more details about the error
        const readyState = eventSourceRef.current?.readyState;
        const stateNames = { 0: 'CONNECTING', 1: 'OPEN', 2: 'CLOSED' };
        console.error(`SSE: Connection error (readyState: ${readyState} = ${stateNames[readyState] || 'UNKNOWN'})`, error);
        setConnected(false);
        setConnectionError('Connection lost');

        // Only reconnect if the connection was actually closed (readyState === 2).
        // Retry indefinitely with capped backoff: a fixed attempt cap meant a
        // laptop sleep, network blip, or backend deploy could leave the tab
        // permanently without live updates until a manual page refresh.
        if (readyState === 2) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          console.log(`SSE: Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current + 1})`);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current++;
            connect(tripId);
          }, delay);
        }
      };

      // Handle different event types
      Object.values(SSEEventType).forEach((eventType) => {
        eventSource.addEventListener(eventType, (event) => {
          try {
            const data = JSON.parse(event.data);
            // Log all events except heartbeats (too noisy)
            if (eventType !== SSEEventType.HEARTBEAT) {
              console.log(`SSE: Received ${eventType} event`, data);
            }
            setLastEvent({ type: eventType, data, timestamp: new Date() });
            notifyListeners(eventType, data);
          } catch (error) {
            console.error(`SSE: Error parsing ${eventType} event:`, error);
          }
        });
      });

      // Handle generic message event
      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type) {
            setLastEvent({ type: data.type, data, timestamp: new Date() });
            notifyListeners(data.type, data);
          }
        } catch (error) {
          console.error('SSE: Error parsing message:', error);
        }
      };

      eventSourceRef.current = eventSource;
    } catch (error) {
      console.error('SSE: Failed to connect', error);
      setConnectionError(error.message);
    }
  }, [user, notifyListeners]);

  // Disconnect from SSE
  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    setConnected(false);
    reconnectAttemptsRef.current = 0;
  }, []);

  // Auto-connect when user is authenticated
  useEffect(() => {
    if (user) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [user, connect, disconnect]);

  // Recover the stream promptly after network loss or tab sleep. Browsers
  // close EventSource on network changes and throttle timers in background
  // tabs, so relying on the backoff timer alone can leave a foregrounded tab
  // stale (missing live inbox/timeline updates) until a manual refresh.
  useEffect(() => {
    const reconnectIfClosed = () => {
      if (user && (!eventSourceRef.current || eventSourceRef.current.readyState === 2)) {
        console.log('SSE: Connection closed while away - reconnecting');
        reconnectAttemptsRef.current = 0;
        connect();
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') reconnectIfClosed();
    };
    window.addEventListener('online', reconnectIfClosed);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('online', reconnectIfClosed);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [user, connect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  const value = {
    connected,
    lastEvent,
    connectionError,
    subscribe,
    connect,
    disconnect,
    SSEEventType,
  };

  return <SSEContext.Provider value={value}>{children}</SSEContext.Provider>;
};

// Custom hooks for specific event subscriptions
export const useStickyNoteEvents = (callback, dependencies = []) => {
  const { subscribe } = useSSE();
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    const cb = (data) => callbackRef.current(data);
    const unsubscribes = [
      subscribe(SSEEventType.STICKY_NOTE_CREATED, cb),
      subscribe(SSEEventType.STICKY_NOTE_UPDATED, cb),
      subscribe(SSEEventType.STICKY_NOTE_DELETED, cb),
    ];

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [subscribe]);
};

export const useTodoEvents = (callback, dependencies = []) => {
  const { subscribe } = useSSE();
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    const cb = (data) => callbackRef.current(data);
    const unsubscribes = [
      subscribe(SSEEventType.TODO_CREATED, cb),
      subscribe(SSEEventType.TODO_UPDATED, cb),
      subscribe(SSEEventType.TODO_DELETED, cb),
    ];

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [subscribe]);
};

export const useUnfiledComponentEvents = (callback, dependencies = []) => {
  const { subscribe } = useSSE();
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    const cb = (data) => callbackRef.current(data);
    const unsubscribes = [
      subscribe(SSEEventType.UNFILED_COMPONENT_CREATED, cb),
      subscribe(SSEEventType.UNFILED_COMPONENT_UPDATED, cb),
      subscribe(SSEEventType.UNFILED_COMPONENT_ASSIGNED, cb),
      subscribe(SSEEventType.UNFILED_COMPONENT_DELETED, cb),
    ];

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [subscribe]);
};

export const useComponentEvents = (callback, dependencies = []) => {
  const { subscribe } = useSSE();
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    const cb = (data) => callbackRef.current(data);
    const unsubscribes = [
      subscribe(SSEEventType.COMPONENT_CREATED, cb),
      subscribe(SSEEventType.COMPONENT_UPDATED, cb),
      subscribe(SSEEventType.COMPONENT_DELETED, cb),
    ];

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [subscribe]);
};

export const useTripEvents = (callback, dependencies = []) => {
  const { subscribe } = useSSE();
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    const cb = (data) => callbackRef.current(data);
    const unsubscribes = [
      subscribe(SSEEventType.TRIP_CREATED, cb),
      subscribe(SSEEventType.TRIP_UPDATED, cb),
      subscribe(SSEEventType.TRIP_DELETED, cb),
    ];

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [subscribe]);
};

/**
 * Hook to subscribe to all flight real-time update events.
 * Callback receives: { type, data: { flight_id, leg_number, update } }
 */
export const useFlightUpdateEvents = (callback, dependencies = []) => {
  const { subscribe } = useSSE();
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    const unsubscribes = [
      subscribe(SSEEventType.FLIGHT_STATUS_UPDATED, (data) => callbackRef.current({ ...data, type: 'status' })),
      subscribe(SSEEventType.FLIGHT_GATE_UPDATED, (data) => callbackRef.current({ ...data, type: 'gate' })),
      subscribe(SSEEventType.FLIGHT_TIME_UPDATED, (data) => callbackRef.current({ ...data, type: 'time' })),
      subscribe(SSEEventType.FLIGHT_DEPARTED, (data) => callbackRef.current({ ...data, type: 'departed' })),
      subscribe(SSEEventType.FLIGHT_ARRIVED, (data) => callbackRef.current({ ...data, type: 'arrived' })),
      subscribe(SSEEventType.FLIGHT_DIVERTED, (data) => callbackRef.current({ ...data, type: 'diverted' })),
      subscribe(SSEEventType.FLIGHT_CANCELLED, (data) => callbackRef.current({ ...data, type: 'cancelled' })),
    ];

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [subscribe]);
};

/**
 * Hook to subscribe to flight updates for a specific flight ID.
 * Automatically filters events to only those matching the provided flight_id.
 */
export const useFlightUpdates = (flightId, callback, dependencies = []) => {
  const wrappedCallback = useCallback((event) => {
    // Only call the callback if the event is for our flight
    if (event.data?.flight_id === flightId) {
      callback(event);
    }
  }, [flightId, callback]);

  useFlightUpdateEvents(wrappedCallback, [flightId, ...dependencies]);
};

/**
 * Hook to subscribe to parsing review queue events (admin).
 * Callback receives: { type, data: { item, deleted_ids? } }
 */
export const useReviewQueueEvents = (callback, dependencies = []) => {
  const { subscribe } = useSSE();
  // Use a ref to always call the latest callback, avoiding stale closure issues
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    const unsubscribes = [
      subscribe(SSEEventType.REVIEW_QUEUE_ITEM_CREATED, (data) => callbackRef.current({ ...data, type: 'created' })),
      subscribe(SSEEventType.REVIEW_QUEUE_ITEM_UPDATED, (data) => callbackRef.current({ ...data, type: 'updated' })),
      subscribe(SSEEventType.REVIEW_QUEUE_ITEM_ASSIGNED, (data) => callbackRef.current({ ...data, type: 'assigned' })),
      subscribe(SSEEventType.REVIEW_QUEUE_ITEM_COMPLETED, (data) => callbackRef.current({ ...data, type: 'completed' })),
      subscribe(SSEEventType.REVIEW_QUEUE_ITEM_DELETED, (data) => callbackRef.current({ ...data, type: 'deleted' })),
      subscribe(SSEEventType.REVIEW_QUEUE_BULK_DELETED, (data) => callbackRef.current({ ...data, type: 'bulk_deleted' })),
    ];

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [subscribe]);
};

/**
 * Hook to subscribe to all Urbanride chauffeur service update events.
 * Callback receives: { type, data: { component_id, urcn, event_type, event_code, ... } }
 */
export const useUrbanrideEvents = (callback, dependencies = []) => {
  const { subscribe } = useSSE();
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    const unsubscribes = [
      subscribe(SSEEventType.URBANRIDE_STATUS_UPDATED, (data) => callbackRef.current({ ...data, type: 'status' })),
      subscribe(SSEEventType.URBANRIDE_CHAUFFEUR_ASSIGNED, (data) => callbackRef.current({ ...data, type: 'chauffeur_assigned' })),
      subscribe(SSEEventType.URBANRIDE_CHAUFFEUR_UNASSIGNED, (data) => callbackRef.current({ ...data, type: 'chauffeur_unassigned' })),
      subscribe(SSEEventType.URBANRIDE_VEHICLE_ON_LOCATION, (data) => callbackRef.current({ ...data, type: 'vehicle_on_location' })),
      subscribe(SSEEventType.URBANRIDE_PASSENGER_ON_BOARD, (data) => callbackRef.current({ ...data, type: 'passenger_on_board' })),
      subscribe(SSEEventType.URBANRIDE_RIDE_COMPLETED, (data) => callbackRef.current({ ...data, type: 'ride_completed' })),
    ];

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [subscribe]);
};

/**
 * Hook to subscribe to Urbanride updates for a specific component.
 * Automatically filters events to only those matching the provided component_id.
 */
export const useUrbanrideComponentUpdates = (componentId, callback, dependencies = []) => {
  const wrappedCallback = useCallback((event) => {
    // Only call the callback if the event is for our component
    if (event.data?.component_id === componentId) {
      callback(event);
    }
  }, [componentId, callback]);

  useUrbanrideEvents(wrappedCallback, [componentId, ...dependencies]);
};

export default SSEContext;
